import type { FilterValue, FilterValues } from "@/lib/table"
import type { components } from "@/types/api.gen"

import { ME, type AssigneeScope } from "./queue-assignee-options"
import { QUEUE_FILTER_IDS as ID } from "./queue-filters"
import { isReviewStatus, type ReviewStatus } from "./queue-review-status"

/*
   Filter panel values → `GET /api/v1/threads` query params.

   The one place a filter becomes a request, so the list and anything else
   that describes the same queue (the tiles, next) send the same thing.

   Every value is checked rather than cast. The values come from the URL, so
   what arrives here is whatever a link — an old one, or a hand-edited one —
   says; anything unrecognised is dropped instead of being sent for the
   backend to refuse with a 422.
*/

type FindingsState = components["schemas"]["FindingsStateFilter"]

const FINDINGS_STATES: readonly FindingsState[] = ["outstanding", "corrected", "none"]

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

/** Named for the wire, so it spreads straight into the request's query params. */
export interface QueueFilterParams {
  status?: ReviewStatus
  /** User IDs, repeated as `assignee_id=1&assignee_id=2` — the backend ORs them. */
  assignee_id?: string[]
  findings_state?: FindingsState
  has_unread?: true
  /** Sent only when asked for; the backend leaves archived reviews out by default. */
  include_archived?: true
  updated_from?: string
  updated_to?: string
}

export interface ThreadsParamsContext {
  /** Who this viewer may name as assignee. Absent: the filter does not apply to them. */
  assignees?: AssigneeScope | undefined
}

export function buildThreadsParams(
  values: FilterValues,
  context: ThreadsParamsContext = {},
): QueueFilterParams {
  const params: QueueFilterParams = {}

  const status = values[ID.status]
  if (isReviewStatus(status)) params.status = status

  const assignees = assigneeIds(values[ID.assignee], context.assignees)
  if (assignees.length > 0) params.assignee_id = assignees

  const findings = values[ID.findings]
  if (isFindingsState(findings)) params.findings_state = findings

  if (values[ID.unread] === true) params.has_unread = true

  if (values[ID.archived] === true) params.include_archived = true

  const range = dateRange(values[ID.updated])
  if (range.from) params.updated_from = range.from
  if (range.to) params.updated_to = range.to

  return params
}

/**
 * `me` becomes the viewer's own id; other values must be numeric ids, and
 * only an admin may send them. De-duplicated, so "Me" plus the admin's own
 * id is one id.
 */
function assigneeIds(value: FilterValue, scope: AssigneeScope | undefined): string[] {
  if (!scope || !Array.isArray(value)) return []
  const ids: string[] = []
  for (const raw of value) {
    if (raw === ME) ids.push(scope.viewerId)
    else if (scope.anyone && /^\d+$/.test(raw)) ids.push(raw)
  }
  return [...new Set(ids)]
}

function isFindingsState(value: unknown): value is FindingsState {
  return typeof value === "string" && (FINDINGS_STATES as readonly string[]).includes(value)
}

/**
 * The picker's whole days, as the instants that bound them in the reviewer's
 * own time zone: `from` at local midnight, `to` at the last millisecond of
 * its day. Sending the bare date would make the backend read `to` as midnight
 * UTC and drop the entire last day from the range.
 *
 * An inverted range is dropped whole — the backend refuses one with a 422,
 * and the picker cannot produce one, so it can only be stale state.
 */
function dateRange(value: FilterValue): { from?: string; to?: string } {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return {}
  const from = typeof value.from === "string" ? localDay(value.from) : null
  const to = typeof value.to === "string" ? localDay(value.to) : null
  if (from && to && from.getTime() > to.getTime()) return {}
  return {
    ...(from ? { from: from.toISOString() } : {}),
    ...(to ? { to: endOfDay(to).toISOString() } : {}),
  }
}

/** `YYYY-MM-DD` → local midnight, or null for anything that is not a real calendar day. */
function localDay(iso: string): Date | null {
  const match = ISO_DATE.exec(iso)
  if (!match) return null
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]
  const date = new Date(year, month - 1, day)
  // `new Date` rolls 2026-02-31 over into March; a day that moved was never a day.
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null
  }
  return date
}

function endOfDay(day: Date): Date {
  const end = new Date(day)
  end.setHours(23, 59, 59, 999)
  return end
}
